import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { getLocalDateInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  OD_FB_FILTERS,
  OD_REF_DAYS_AGO_VALUES,
} from 'lumen/utils/constants/overview-dashboard-constants';
import { parseObject } from 'lumen/utils/parse-utils';

export default class OdSelectionsService extends Service {
  @service('hotel-and-market') hotelAndMarketService;

  @tracked activeFilters = DEFAULT_QP_VALUES.hotel.overview.activeFilters;
  // String containing the fromDate used in the datepicker range (initally set via route, then updated via controller)
  @tracked datepickerFromDate = null;
  // String containing the toDate used in the datepicker range (initally set via route, then updated via controller)
  @tracked datepickerToDate = null;
  @tracked refDaysAgoDatesInAPIFormat = {};
  @tracked selectedRefDaysAgo = null;

  get activeDrilldownDayAsString() {
    return this.parsedActiveFilters?.[OD_FB_FILTERS.DAY];
  }

  get dateRangeForKpisWithForwardLookingData() {
    const todayMoment = getLocalDateInBrowserTimezone(
      this.hotelAndMarketService.timezone,
    );

    // Selected daterange is all in the past. There's nothing useful we can do for KPIs that only allow forward looking data
    if (
      dayjs(this.datepickerFromDate).isBefore(todayMoment, 'day') &&
      dayjs(this.datepickerToDate).isBefore(todayMoment, 'day')
    ) {
      return {
        start: null,
        end: null,
      };
    }
    if (dayjs(this.datepickerFromDate).isBefore(todayMoment, 'day')) {
      // If the rangeStart date is in the past, we can create an updated daterange where we use the current day as the starting day.
      return {
        start: todayMoment.format('YYYY-MM-DD'),
        end: this.datepickerToDate,
      };
    }
    // If we arrive at this point it means that the selected daterange only contains days in the future. We can use this range for all KPIs with forward looking data as well
    return {
      start: this.datepickerFromDate,
      end: this.datepickerToDate,
    };
  }

  get isDayDrilldownActive() {
    return Boolean(this.parsedActiveFilters?.[OD_FB_FILTERS.DAY]);
  }

  // Helper getter to quickly know if a single day is selected in the range datepicker of the page (so not a day drilldown, but only data of 1 day is shown in the dashboard)
  get isSingleDaySelectedInTheDatepicker() {
    return this.datepickerFromDate === this.datepickerToDate;
  }

  get parsedActiveFilters() {
    if (this.activeFilters) {
      return parseObject(this.activeFilters);
    }
    return {};
  }

  /*
    Added this here because we need to fallback to today instead of the start of the selected range in the datepicker multiple times
    Things impacted: KPI route requests, top days components in certain KPIs
    e.g.: if current day is 17/12, the user can now select the current month, which will select the range of 01/12 until 31/12 (first time showing past data)
    - Performance KPIs can show this past data, but Market & pricing KPIs not.

    for Performance KPIs this means we'll launch an API request with range of 01/12 - 31/12
    for Market & Pricing KPIs we'll need to launch an API request with range of 17/12 - 31/12 (this fallback can be used)

    The top days components are also impacted. Mainly the Performance KPI ones. In this case we'll receive 31 from dates, but we only want to show future dates in the top days list.
    This fallback can be used for an extra filter check in that code
  */
  get rangeStartTodayFallbackAsString() {
    return this.hotelAndMarketService.getLocalTime().format('YYYY-MM-DD');
  }

  clearValues() {
    this.activeFilters = DEFAULT_QP_VALUES.hotel.overview.activeFilters;
    this.selectedRefDaysAgo = null;
    this.refDaysAgoDatesInAPIFormat = {};
  }

  setRefDaysAgoDatesInAPIFormat() {
    const todayMoment = getLocalDateInBrowserTimezone(
      this.hotelAndMarketService.timezone,
    );

    this.refDaysAgoDatesInAPIFormat[OD_REF_DAYS_AGO_VALUES.yesterday] =
      todayMoment.subtract(1, 'day').format('YYYY-MM-DD');
    this.refDaysAgoDatesInAPIFormat[OD_REF_DAYS_AGO_VALUES.threeDaysAgo] =
      todayMoment.subtract(3, 'day').format('YYYY-MM-DD');
    this.refDaysAgoDatesInAPIFormat[OD_REF_DAYS_AGO_VALUES.sevenDaysAgo] =
      todayMoment.subtract(7, 'day').format('YYYY-MM-DD');
    this.refDaysAgoDatesInAPIFormat[OD_REF_DAYS_AGO_VALUES.fourteenDaysAgo] =
      todayMoment.subtract(14, 'day').format('YYYY-MM-DD');
  }
}
